import { Settings2 } from '@tamagui/lucide-icons';
import { Pressable } from '@unistocks/components/basic/pressable';
import { Heading } from '@unistocks/components/text/text';
import { useIsDashboardItemFetching } from '@unistocks/features/dashboard/hooks/use-is-dashboard-item-fetching';
import { DashboardItemDto } from '@unistocks/proxy/axios-client';
import { PRESSABLE_SCALE } from '@unistocks/shared/consts';
import { withFeedback } from '@unistocks/shared/utils/haptics/with-feedback';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import { scheduleOnRN } from 'react-native-worklets';
import { CardHeader, GetProps, isWeb, Spinner, styled } from 'tamagui';

export type DashboardItemHeaderProps = Omit<GetProps<typeof Header>, 'onPress'> & {
  item: DashboardItemDto;
  title?: string;
  onPress?: () => void;
  openSettings: (item: DashboardItemDto, title?: string) => void;
};

export const Header = styled(CardHeader, {
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  mb: '$2'
});

export const DashboardItemHeader: React.FC<DashboardItemHeaderProps> = ({
  item,
  title,
  openSettings,
  ...props
}) => {
  const isFetching = useIsDashboardItemFetching(item);
  const clickable = !!props.onPress;

  // Gesture handler that cancels if user moves more than 10 pixels
  const tapGesture = Gesture.Tap()
    .maxDuration(Number.MAX_SAFE_INTEGER)
    .maxDistance(10)
    .onStart(() => {
      if (clickable) {
        scheduleOnRN(withFeedback);
      }
    })
    .onEnd((e, success) => {
      if (success && props.onPress) scheduleOnRN(props.onPress);
    });

  const heading = (
    <Heading
      ellipsis
      numberOfLines={1}
      whiteSpace="nowrap"
      transition={'quicker'}
      className={clickable ? 'prevent-drag' : undefined}
      pressStyle={clickable ? { scale: PRESSABLE_SCALE } : undefined}
      onPress={isWeb && clickable ? props.onPress : undefined}
      $platform-web={clickable ? { cursor: 'pointer' } : undefined}
    >
      {title}
    </Heading>
  );

  return (
    <Header {...props} className="drag-handle" onPress={undefined}>
      {title ? (
        isWeb ? heading : <GestureDetector gesture={tapGesture}>{heading}</GestureDetector>
      ) : null}

      <Pressable
        withFeedback
        withScale
        hitSlop={20}
        ml={'auto'}
        onPress={() => openSettings(item, title)}
      >
        {isFetching ? (
          <Spinner color={'$gray9'} />
        ) : (
          <Settings2 size={'$1.5'} color={'$unistocks'} />
        )}
      </Pressable>
    </Header>
  );
};

DashboardItemHeader.displayName = 'DashboardItemHeader';
